import * as React from 'react';
import { DetailField } from './DetailField';
import { Inline, type InlineProps } from './Layout';

export interface VehicleRowProps
  extends Omit<InlineProps, 'children' | 'color'> {
  make?: string | null;
  model?: string | null;
  year?: string | number | null;
  color?: string | null;
  /**
   * Miles. A number is grouped — 41,200; a string is shown as it is. Left
   * out (undefined) for a record that never carries one, such as an impound
   * or a decoded VIN, the fact is not drawn; null is a blank, and a dash.
   */
  odometer?: string | number | null;
  plate?: string | null;
}

type VehicleFactsOf = Pick<
  VehicleRowProps,
  'make' | 'model' | 'year' | 'color' | 'odometer' | 'plate'
>;

/**
 * The facts in the order the Figma lists them, each a label and its value,
 * the odometer grouped and left out when it is not passed. VehicleCard
 * lists the same ones, under the VIN.
 */
export const vehicleFacts = ({
  make,
  model,
  year,
  color,
  odometer,
  plate,
}: VehicleFactsOf): [string, unknown][] => [
  ['Make', make],
  ['Model', model],
  ['Year', year],
  ['Color', color],
  ...(odometer === undefined
    ? []
    : [
        [
          'ODO',
          typeof odometer === 'number'
            ? new Intl.NumberFormat('en-US').format(odometer)
            : odometer,
        ] as [string, unknown],
      ]),
  ['Plate', plate],
];

/**
 * The vehicle on a call, in six facts in a row that wraps: make, model,
 * year, colour, odometer and plate, each a label over its value, as the
 * Figma's call detail draws them.
 *
 *   <VehicleRow make="Ford" model="F-150" year={2019} color="Silver"
 *     odometer={41200} plate="8ABC123" />
 *
 * Blank facts read as a dash rather than closing up, so a fact is always in
 * the same place. The odometer alone is left out when it is not passed: an
 * impound and a decoded VIN have none, and a dash would say it is unknown.
 * VehicleCard is the same facts one under another with the VIN first, in an
 * outline: the call's Vehicle section. On a desk,
 * VehicleInformationSectionView is the titled card with the type and VIN
 * as well.
 */
export const VehicleRow = React.forwardRef<HTMLDivElement, VehicleRowProps>(
  function VehicleRow(
    { make, model, year, color, odometer, plate, gap = 4, ...props },
    ref
  ) {
    const facts = vehicleFacts({ make, model, year, color, odometer, plate });
    return (
      <Inline ref={ref} gap={gap} wrap align="start" {...props}>
        {facts.map(([label, value]) => (
          <DetailField
            key={label}
            label={label}
            value={value as React.ReactNode}
          />
        ))}
      </Inline>
    );
  }
);
